/**
 * Sidebar — Navigation sidebar for desktop, drawer for mobile.
 * issue_137: Added conditional organization menu items for ORGANIZATION_ADMIN role.
 * issue_138: Added conditional member menu items for ORGANIZATION_MEMBER role.
 * issue_139: Hide main nav items for org profiles (only Dashboard + Settings remain).
 * issue_155: Show organization logo and name in the brand area for org users.
 * issue_159: Renamed "Acquisti" to "Assegna Corsi" and changed path to /organization/enrolls.
 * issue_161: Hide Dashboard menu item for ORGANIZATION_ADMIN (orgAdminHidden flag).
 * issue_163: USER (pending) sees only Settings + onboarding banner.
 *            CREATOR/ADMIN see all nav items (same as ex-USER behavior).
 * issue_04: Added "Audit Trail" nav item for ORGANIZATION_ADMIN (ScrollText icon).
 * issue_05: Added "Compliance" nav item for ORGANIZATION_ADMIN (ShieldCheck icon).
 * issue_214: Raggruppate le voci corso (Iscritti, Catalogo Corsi, Assegna Corsi)
 *            sotto un sottogruppo "Corsi" collassabile/espandibile nella sidebar.
 * issue_218: Aggiunte voci di menu "Questionari" per CREATOR/ADMIN e
 *            "Questionari", "Storico Assessment", "Non Conformità" per ORGANIZATION_ADMIN.
 */

import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  LayoutDashboard,
  Layers,
  GraduationCap,
  Target,
  Share2,
  Settings,
  X,
  Building2,
  Users,
  BookOpen,
  ClipboardList,
  ClipboardCheck,
  TrendingUp,
  ScrollText,
  ShieldCheck,
  Activity,
  History,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '@/features/auth/auth.hook';
import { isOrganizationProfile, isPendingUser } from '@/features/auth/role-utils';
import { useMyOrgInfo } from '@/features/organizations/hooks/useOrganizations';
import { api } from '@/lib/api/client';

export interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const allNavItems = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true, orgVisible: true, orgAdminHidden: true },
  { to: '/decks', label: 'Unità Didattiche', icon: Layers, end: false, orgVisible: false },
  { to: '/study', label: 'Study', icon: GraduationCap, end: false, orgVisible: false },
  { to: '/goals', label: 'Goals', icon: Target, end: false, orgVisible: false },
  { to: '/sharing', label: 'Sharing', icon: Share2, end: false, orgVisible: false },
  // issue_218: "Questionari" per CREATOR/ADMIN
  { to: '/assessments', label: 'Questionari', icon: ClipboardCheck, end: false, orgVisible: false },
  { to: '/settings', label: 'Settings', icon: Settings, end: false, orgVisible: true },
];

// issue_214: Voci organizzazione non-corso (rimangono al livello principale)
const organizationNavItems = [
  { to: '/organization', label: 'La mia organizzazione', icon: Building2, end: true },
  { to: '/organization/users', label: 'Utenti', icon: Users },
  { to: '/organization/audit-trail', label: 'Audit Trail', icon: ScrollText },
  { to: '/organization/compliance', label: 'Compliance', icon: ShieldCheck },
  // issue_218: Voci assessment per ORGANIZATION_ADMIN
  { to: '/organization/assessments', label: 'Questionari', icon: ClipboardCheck },
  { to: '/organization/assessments/history', label: 'Storico Assessment', icon: History },
  { to: '/organization/non-conformities', label: 'Non Conformità', icon: AlertTriangle },
];

// issue_214: Voci corso-related estratte in array separato per il sottogruppo "Corsi"
const courseNavItems = [
  { to: '/organization/enrollments', label: 'Iscritti', icon: ClipboardList },
  { to: '/organization/catalog', label: 'Catalogo Corsi', icon: BookOpen },
  { to: '/organization/enrolls', label: 'Assegna Corsi', icon: ClipboardList },
];

const memberNavItems = [
  { to: '/my-courses', label: 'I miei corsi', icon: BookOpen, end: true },
  { to: '/my-progress', label: 'I miei progressi', icon: TrendingUp },
];

const adminNavItems = [
  { to: '/admin/telemetry', label: 'Telemetria', icon: Activity },
];

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { user } = useAuth();
  const isOrgAdmin = user?.role === 'organization-admin';
  const isOrgMember = user?.role === 'organization-member';
  const isOrg = isOrganizationProfile(user?.role);
  const isPending = isPendingUser(user?.role);

  // issue_214: Stato per il sottogruppo "Corsi" (default espanso)
  const [coursesExpanded, setCoursesExpanded] = useState(true);

  // Fetch org info (name + logoUrl) for org users.
  // Called unconditionally to satisfy Rules of Hooks; result is only
  // used when isOrg is true. Non-org users will get a 404 which
  // react-query handles silently (the error is cached but we ignore it).
  const { data: orgInfo } = useMyOrgInfo();

  // Logo blob URL — fetched via authenticated API client so the JWT
  // header is included. The <img src> tag cannot send auth headers,
  // so we fetch the logo as a blob and create an object URL.
  const [logoBlobUrl, setLogoBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (isOrg && orgInfo?.logoUrl) {
      let objectUrl: string | null = null;
      let revoked = false;

      // issue_156: Append a cache-buster query param using updatedAt so the
      // browser always fetches the latest logo version after an update.
      const logoUrlWithBuster =
        orgInfo.logoUrl + '?v=' + (orgInfo.updatedAt ?? Date.now());

      api
        .getBlob(logoUrlWithBuster)
        .then((blob) => {
          objectUrl = URL.createObjectURL(blob);
          if (!revoked) {
            setLogoBlobUrl(objectUrl);
          } else {
            URL.revokeObjectURL(objectUrl);
          }
        })
        .catch(() => {
          setLogoBlobUrl(null);
        });

      return () => {
        revoked = true;
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
      };
    } else {
      setLogoBlobUrl(null);
    }
  }, [isOrg, orgInfo?.logoUrl, orgInfo?.updatedAt]);

  // Filter nav items based on user role:
  // - issue_163: USER (pending) → only Settings
  // - Org profiles: only items with orgVisible=true (and exclude orgAdminHidden for org admin)
  // - CREATOR/ADMIN (default): all nav items
  const navItems = isPending
    ? allNavItems.filter((item) => item.to === '/settings')
    : isOrg
      ? allNavItems.filter(
          (item) => item.orgVisible && !(isOrgAdmin && item.orgAdminHidden),
        )
      : allNavItems;

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed lg:sticky top-0 left-0 z-50 h-screen w-64 bg-white border-r border-gray-200 flex flex-col transition-transform duration-300 lg:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Main navigation"
      >
        {/* Logo / brand */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-100">
          <NavLink to="/" className="flex items-center gap-2" onClick={onClose}>
            {isOrg && logoBlobUrl ? (
              <img
                src={logoBlobUrl}
                alt={orgInfo?.name ?? 'Logo'}
                className="w-8 h-8 rounded-lg object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-primary-600 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
            )}
            <span className="font-bold text-gray-900 text-lg">
              {isOrg && orgInfo?.name ? orgInfo.name : 'imparo360.it'}
            </span>
          </NavLink>
          <button
            onClick={onClose}
            className="lg:hidden text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100"
            aria-label="Close navigation"
          >
            <X size={20} />
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1 no-scrollbar">
          {/* issue_163: Onboarding banner for pending users */}
          {isPending && (
            <div className="mb-4 px-3 py-3 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-xs text-amber-800 font-medium mb-2">
                Completa la configurazione del tuo account
              </p>
              <NavLink
                to="/onboarding"
                onClick={onClose}
                className="text-xs text-amber-700 hover:text-amber-900 font-semibold underline"
              >
                Vai al setup →
              </NavLink>
            </div>
          )}

          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={onClose}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                  )
                }
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}

          {/* Organization section — visible only to ORGANIZATION_ADMIN */}
          {isOrgAdmin && (
            <>
              <div className="pt-4 pb-1 px-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                  Organizzazione
                </span>
              </div>

              {/* Voci organizzazione non-corso (livello principale) */}
              {organizationNavItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={onClose}
                    className={({ isActive }) =>
                      clsx(
                        'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                      )
                    }
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}

              {/* issue_214: Sottogruppo "Corsi" collassabile/espandibile */}
              <div className="pt-2">
                {/* Header cliccabile del sottogruppo */}
                <button
                  type="button"
                  onClick={() => setCoursesExpanded((prev) => !prev)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors w-full"
                  aria-expanded={coursesExpanded}
                  aria-controls="courses-submenu"
                >
                  <BookOpen className="w-5 h-5 flex-shrink-0" />
                  <span>Corsi</span>
                  {coursesExpanded ? (
                    <ChevronDown className="w-4 h-4 ml-auto text-gray-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 ml-auto text-gray-400" />
                  )}
                </button>

                {/* Voci del sottogruppo (con indentazione) */}
                {coursesExpanded && (
                  <div id="courses-submenu" className="mt-0.5 space-y-1">
                    {courseNavItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          end={item.end}
                          onClick={onClose}
                          className={({ isActive }) =>
                            clsx(
                              'flex items-center gap-3 pl-9 pr-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                              isActive
                                ? 'bg-primary-50 text-primary-700'
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                            )
                          }
                        >
                          <Icon className="w-5 h-5 flex-shrink-0" />
                          <span>{item.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Member section — visible to ORGANIZATION_MEMBER and ORGANIZATION_ADMIN */}
          {(isOrgMember || isOrgAdmin) && (
            <>
              <div className="pt-4 pb-1 px-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                  I miei corsi
                </span>
              </div>
              {memberNavItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={onClose}
                    className={({ isActive }) =>
                      clsx(
                        'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                      )
                    }
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </>
          )}

          {/* Admin section — visible only to ADMIN role */}
          {user?.role === 'admin' && (
            <>
              <div className="pt-4 pb-1 px-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                  Amministrazione
                </span>
              </div>
              {adminNavItems.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={onClose}
                    className={({ isActive }) =>
                      clsx(
                        'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                      )
                    }
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </>
          )}
        </nav>

        {/* Footer */}
        <div className="px-4 py-4 border-t border-gray-100">
          <p className="text-xs text-gray-400 text-center">
            imparo360.it
            <br />
            v1.0.0
          </p>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;